import { optionsOf, sameValue, optionDisplay } from './kinds.js'
import { markClasses, markDotClasses } from '../ui/control.js'

/**
 * DS-2 §3.2 — ONE component for ordinal, binary and categorical. Three kinds,
 * two layouts, one set of rules.
 *
 * Ordinal alone is 33 of the 43 formats, so this is most of what a participant
 * ever touches: roughly 20 taps per beep, 30 beeps, plus the whole day-0
 * battery.
 *
 * NON-NEGOTIABLE, and all of these predate this component:
 *  - options render in CONFIG ORDER. Never sorted, never reversed.
 *  - the stored value is the option's `value`, NEVER its index. AUDIT 9 and 10
 *    score 0/2/4; EPDS 3 and 5-10 run 3,2,1,0, so index and value genuinely
 *    run in opposite directions.
 *  - an option with an empty label falls back to its own value.
 *  - every option is identical in every state but position and label. No token
 *    may be keyed on index, value, or reverse-scored flag.
 *
 * BINARY GETS NO SPECIAL STYLING. No yes/no colour, no thumbs, no icons. "Yes"
 * to "have you intentionally hurt yourself?" must look exactly like "Yes" to
 * "did you nap today?".
 */

/**
 * AD-3 §4.1 — OUTSIDE A GRID, EVERY OPTION SET IS A GROUP.
 *
 * The N-across row is retired here and survives only inside OptionGrid, where
 * a shared sticky header draws the labels once and the cells carry marks. On a
 * single-item screen the row was asking a participant to read five stacked
 * fragments of text across 358px; the group gives each option a full line.
 *
 * That also removes the whole class of problem F8 lived in. A row had to
 * reflow, break words and reserve a minimum cell; a group is one column and
 * cannot overflow horizontally at any text size.
 *
 * The old ROW_MAX_OPTIONS / ROW_MAX_LABEL thresholds are gone rather than
 * unused: they decided a question this function no longer asks, and a constant
 * that decides nothing is how the old rule comes back.
 */
export function preferredLayout() {
  return 'group'
}

function Mark({ selected, disabled }) {
  return (
    <span className={markClasses({ selected, disabled })} aria-hidden="true">
      <span className={markDotClasses({ selected, disabled })} />
    </span>
  )
}

export function OptionSet({
  item,
  format,
  answer,
  onChange,
  disabled,
  name,
  // 'row' | 'card' | undefined (choose from the format)
  layout,
  // A grid draws its column labels once in the sticky header, so the cells
  // carry marks only.
  /*
   * The narrowest a row cell may get before the row wraps. Default is tuned for
   * SCALES and is deliberately tight — measured at 390px, it is the largest
   * value that still keeps a 7-option scale on one row at 100%, gaps included.
   * Raising it wraps battery scales; do not change it without re-measuring.
   */
  minCell = '2.5em',
  /*
   * The accessible name for the group, and ONLY when something else is not
   * already providing one. When the item renders its prompt as a <legend>, the
   * fieldset names the group; adding aria-label here would override the legend
   * with a duplicate. In a grid cell there is no legend, so the row's prompt is
   * passed in instead — by REFERENCE, so the words exist once in the document.
   */
  ariaLabelledBy,
}) {
  const options = optionsOf(format)
  const selected = answer?.value
  const mode = layout ?? preferredLayout()
  const groupName = name ?? item?.item_id

  const choose = (option) => () => onChange({ ...answer, value: option.value })

  if (mode === 'row') {
    /*
     * THE FLOOR, AND WHY IT IS A PARAMETER RATHER THAN A CONSTANT.
     *
     * A SCALE and a CHOICE LIST want different answers here, and trying to
     * serve both with one number made each one worse in turn.
     *
     * A scale's row IS the control: five anchors read left to right, compared
     * against each other. Breaking it across lines costs the comparison, so the
     * floor stays tight (2.5em) and every battery scale keeps exactly the
     * shape it has today at 100%. Its anchors are short, so nothing chops.
     *
     * A choice list has no such reading. Demographics' labels are single long
     * words — "Postgraduate", "Unemployed", "Homemaker" — and a tight floor
     * made cells narrower than the words, so the wrap guard chopped them into
     * "Postgra/duate". Those screens pass a wider floor, wrap to two rows, and
     * stay far shorter than the stacked cards they replaced.
     *
     * `ch` and `em` both scale with her text size, so F8's reflow holds either
     * way: bigger text always means fewer columns, never a collision.
     */
    const floor = minCell
    /*
        F8 — THE ROW MUST REFLOW WHEN THE TEXT GROWS.

        This was `repeat(N, minmax(0, 1fr))`: N columns, always, whatever the
        text size. At a 200% system font the labels collided into each other and
        rendered as "ModeMarkExtreme" — on the self-harm item, which is the
        worst screen in the app for it.

        The type scaled correctly; the LAYOUT did not, and it failed in the
        compounding direction. The container is capped in `em`, so raising the
        root font made the row NARROWER (324px to 260px) at the same moment it
        made the words wider. Cells went 62px to 46px while "Moderate" needed
        about 130px.

        Two independent guards, because either alone still breaks:

        1. `auto-fit` with an `em` minimum. The floor scales with her font size,
           so when the words get bigger the row keeps fewer columns and the rest
           wrap to the next line.

           2.5em is chosen so a 7-option scale still fits ONE row at the default
           size — nothing about the normal case changes. The first value tried,
           2.75em, silently wrapped 7-option scales at 100% because it ignored
           the six inter-cell gaps: 7 x 44px + 6 x 4px is 332 against a 324px
           row. Measured rather than reasoned, and the arithmetic is the whole
           margin, so do not raise this without re-measuring at 390px.

        2. `overflow-wrap: anywhere` on the label. auto-fit alone cannot save a
           single word that is wider than any cell it will ever get; without
           this, "Moderate" still spills. Together they make collision
           impossible at any size.

        Every column stays exactly equal, which is the part that mattered: once
        labels collide the options stop being equally legible, and that is the
        neutrality rule failing by layout instead of by colour.
      */
    return (
      <div
        role="radiogroup"
        aria-labelledby={ariaLabelledBy}
        className="grid gap-[var(--s-1)]"
        style={{ gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${floor}), 1fr))` }}
      >
        {options.map((option) => {
          const isSelected = sameValue(selected, option.value)
          const { text } = optionDisplay(option)
          return (
            <label
              key={String(option.value)}
              data-selected={isSelected ? '1' : undefined}
              className={
                'OptionCell flex min-h-[max(48px,3rem)] min-w-0 flex-col items-center justify-center gap-[var(--s-1)] ' +
                'rounded-[var(--r-control)] px-1 py-2 text-center ' +
                /*
                 * §4.2 — A GRID CELL HAS NO BORDER AND NO BACKGROUND, and the
                 * 22px mark IS the control boundary.
                 *
                 * Removing the per-cell border is the single biggest visual
                 * change on these screens and it is safe: the mark carries
                 * --control-border at 3.64:1, which satisfies the 3:1 boundary
                 * requirement without drawing thirty boxes. It also matches
                 * what the instrument actually is — a paper questionnaire has
                 * circles to fill, not boxes.
                 *
                 * Only when the labels are in the header, i.e. only in a grid.
                 * Everywhere else `layout='row'` is not used at all any more
                 * (step 4 retired it), so this branch is grid-only in practice.
                 */
                /*
                 * A CHOSEN CELL GETS A TINT, NOT JUST A FILLED DOT. Across a
                 * six-column row the 22px mark alone is a small signal, and
                 * "which one did I pick" should be answerable from the row
                 * shape at a glance. --accent-tint is the same signal the
                 * consent affirmations use for "done"; the mark still carries
                 * the boundary, so this is a second channel, not a replacement.
                 */
                ('cursor-pointer active:bg-[var(--surface-sunken)] ' +
                  (isSelected ? 'bg-[var(--accent-tint)]' : ''))
              }
            >
              <input
                type="radio"
                name={groupName}
                value={String(option.value)}
                checked={isSelected}
                onChange={choose(option)}
                disabled={disabled}
                aria-label={text}
                className="sr-only"
              />
              <Mark selected={isSelected} disabled={disabled} />
            </label>
          )
        })}
      </div>
    )
  }

  /*
   * §4.1 — ONE SURFACE, ROWS INSIDE IT.
   *
   * Thirty floating bordered boxes become one object with divisions. The group
   * carries the border; the rows carry --rule separators and nothing of their
   * own, so the eye reads a list rather than a scatter.
   *
   * THE GROUP MUST NOT CLIP. `overflow: visible` is load-bearing: a hidden
   * overflow here would eat the focus ring, which §2.3 forbids outright. The
   * end rows get cut corners instead, at --r-surface-inner (11px = 12 minus the
   * group's own 1px border) — get that wrong and a visible sliver appears at
   * each corner.
   */
  return (
    <div
      role="radiogroup"
      aria-labelledby={ariaLabelledBy}
      className={
        'flex flex-col overflow-visible rounded-[var(--r-surface)] ' +
        'border border-[var(--line)] bg-[var(--surface-raised)]'
      }
    >
      {options.map((option, i) => {
        const isSelected = sameValue(selected, option.value)
        const { text, isFallback } = optionDisplay(option)
        return (
          <label
            key={String(option.value)}
            data-selected={isSelected ? '1' : undefined}
            className={
              'OptionRow relative flex min-h-[max(48px,3.5rem)] items-center gap-[0.875rem] overflow-hidden ' +
              'px-[var(--s-4)] py-[var(--s-3)] ' +
              (i === 0 ? '' : 'border-t border-[var(--rule)] ') +
              (i === 0 ? 'rounded-t-[var(--r-surface-inner)] ' : '') +
              (i === options.length - 1 ? 'rounded-b-[var(--r-surface-inner)] ' : '') +
              (isSelected ? 'bg-[var(--accent-tint)] ' : '') +
              (disabled ? 'text-[var(--ink-500)]' : 'active:bg-[var(--surface-sunken)]')
            }
          >
            {/*
              THE THIRD CHANNEL (§1, candidate E'). Selection is carried by
              THREE things at once — the filled mark, the tinted row, and this
              3px leading bar — so it never depends on colour alone.
              `overflow: hidden` on the row is what clips the bar to the
              rounded corner on the end rows.

              IN FORCED-COLORS the tint and this bar are both flattened —
              white on white — so neither carries anything there. What survives
              is the mark's 1px-to-2px ring, plus the inset CanvasText outline
              added in index.css.

              THAT OUTLINE WAS MISSING UNTIL AD-7. This comment previously
              claimed it as a channel while no @media (forced-colors: active)
              block existed anywhere in the build: my §7.4 gate had proved the
              rule worked in a PROTOTYPE and I never shipped the rule. A comment
              asserting a channel nobody has is how the next person stops
              checking, which is exactly what happened.
            */}
            {isSelected ? (
              <span
                aria-hidden="true"
                className="absolute bottom-0 left-0 top-0 w-[3px] bg-[var(--accent)]"
              />
            ) : null}
            <input
              type="radio"
              name={groupName}
              value={String(option.value)}
              checked={isSelected}
              onChange={choose(option)}
              disabled={disabled}
              className="sr-only"
            />
            <Mark selected={isSelected} disabled={disabled} />
            <span className={'t-option ' + (isFallback ? 'tabular-nums text-[var(--ink-500)]' : '')}>
              {text}
            </span>
          </label>
        )
      })}
    </div>
  )
}
